import './ui/styles.css';
import { UIComponents } from './ui/components';
import { syncService } from './services/sync';
import { authService } from './services/auth';
import { barcodeScanner } from './services/scanner';
import { listarCampus } from './data/campus';
import { ModuloInventario } from './modules/inventario';
import { ModuloVerificacao } from './modules/verificacao';
import { ModuloLaudo } from './modules/laudo';
import { ModuloMovimentacao } from './modules/movimentacao';
import { ModuloUsuarios } from './modules/usuarios';
import { ModuloLocais } from './modules/locais';
import type { AppState, Campus } from './types';

// Estado global da aplicação
const state: AppState = {
  session: null,
  campus: null,
  modulo: null,
  sede: null,
  bloco: null,
  andar: null,
  sala: null,
  salaSetor: '',
  salaDescricao: '',
  bemBase: null,
  semTombo: false,
  danificado: false,
  descricaoNaoConfere: false,
  locaisDb: [],
  moduloBaseLiberado: false,
  moduloVerificacaoLiberado: false,
  verifView: null,
  laudoBemConfirmado: false,
  laudoLocalConfere: true,
  laudoBem: null,
  editLocalId: null,
};

// Instâncias dos módulos
let moduloInventario: ModuloInventario;
let moduloVerificacao: ModuloVerificacao;
let moduloLaudo: ModuloLaudo;
let moduloMovimentacao: ModuloMovimentacao;
let moduloUsuarios: ModuloUsuarios;
let moduloLocais: ModuloLocais;

// Helpers
const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
type ModoSelecaoCampus = 'todos' | 'permitidos';
let modoSelecaoCampus: ModoSelecaoCampus = 'todos';

const CAMPUS_COORDS: Record<string, { lat: number; lon: number }> = {
  ap: { lat: -23.5508, lon: -51.4636 },
  cm: { lat: -24.0463, lon: -52.3780 },
  cp: { lat: -23.1813, lon: -50.6465 },
  ct: { lat: -25.4394, lon: -49.2683 },
  dv: { lat: -25.7333, lon: -53.0570 },
  fb: { lat: -26.0817, lon: -53.0535 },
  gp: { lat: -25.3902, lon: -51.4623 },
  ld: { lat: -23.3045, lon: -51.1696 },
  md: { lat: -25.2953, lon: -54.0944 },
  pb: { lat: -26.2298, lon: -52.6711 },
  pg: { lat: -25.0950, lon: -50.1619 },
  sh: { lat: -24.8585, lon: -54.3360 },
  td: { lat: -24.7246, lon: -53.7412 },
};

function startGlobalLoading() {
  document.getElementById('global-loading-bar')?.classList.add('active');
}

function stopGlobalLoading() {
  document.getElementById('global-loading-bar')?.classList.remove('active');
}

function showView(viewId: string) {
  const views = [
    'view-campus',
    'view-modulos',
    'view-localizacao',
    'view-inventario',
    'view-movimentacao',
    'view-verificacao',
    'view-laudo',
    'view-base-dados',
    'view-locais',
    'view-usuarios'
  ];

  views.forEach(v => {
    const el = byId(v);
    if (el) el.classList.toggle('active', v === viewId);
  });

  document.body.classList.toggle(
    'compact-mode',
    viewId !== 'view-campus' && viewId !== 'view-modulos'
  );

  document.body.classList.toggle('home-view', viewId === 'view-campus');
}

// ========== INICIALIZAÇÃO ==========
async function init() {
  const loading = UIComponents.showLoading('Inicializando aplicação...');

  try {
    await syncService.init();
    console.log('✅ Serviços inicializados');

    state.session = await authService.getCurrentSession();
    atualizarAuthUI();

    popularCampus();

    byId('btn-trocar-campus')?.addEventListener('click', trocarCampusPermitido);
    byId('app-title-btn')?.addEventListener('click', voltarParaCampus);
    byId('campus-nome-header')?.addEventListener('click', voltarParaModulos);

    setupScrollReveal();

    moduloInventario = new ModuloInventario(state);
    moduloVerificacao = new ModuloVerificacao(state);
    moduloLaudo = new ModuloLaudo(state);
    moduloMovimentacao = new ModuloMovimentacao(state);
    moduloUsuarios = new ModuloUsuarios(state);
    moduloLocais = new ModuloLocais(state);

    configurarScanner();
    configurarAuth();

    window.addEventListener('app:show-view', ((e: CustomEvent) => {
      showView(e.detail);
    }) as EventListener);
    
    window.addEventListener('popstate', processarHash);

    const hashProcessado = await processarHash();

    if (!hashProcessado && !state.campus && state.session) {
      await direcionarAposLogin();
    }

    if (!hashProcessado && !state.campus && !state.session) {
      await selecionarCampusPorLocalizacao();
    }

    if (!state.campus) {
      document.body.classList.add('home-view');
    }

  } catch (error) {
    console.error('❌ Erro ao inicializar aplicação:', error);
    UIComponents.error((error as Error).message || 'Erro ao inicializar aplicação. Recarregue a página.');
  } finally {
    UIComponents.hideLoading(loading);
    console.log('✅ Aplicação finalizou a inicialização.');
  }
}

// ========== CAMPUS ==========
function popularCampus() {
  const grid = byId('campus-grid');
  if (!grid) return;

  grid.innerHTML = '';
  const titulo = byId('campus-panel')?.querySelector('h2');
  const campusList = campusDisponiveisParaSelecao();

  if (titulo) {
    titulo.textContent = modoSelecaoCampus === 'permitidos'
      ? 'Selecione seu campus de acesso'
      : 'Selecione sua Unidade';
  }

  if (!campusList.length) {
    grid.innerHTML = '<div class="result-box">Seu usuário não possui campus liberado.</div>';
    return;
  }

  campusList.forEach(campus => {
    const card = criarCardCampus(campus);
    card.addEventListener('click', () => selecionarCampus(campus));
    grid.appendChild(card);
  });
}

function campusDisponiveisParaSelecao() {
  const todos = listarCampus();
  if (modoSelecaoCampus !== 'permitidos') return todos;
  return todos.filter(campus => authService.canAccessCampus(state.session, campus.sigla));
}

function campusPermitidosLogin() {
  return listarCampus().filter(campus => authService.canAccessCampus(state.session, campus.sigla));
}

function criarCardCampus(campus: Campus): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'card campus-card';
  btn.dataset.campusId = campus.id;

  const foto = campus.foto
    ? `<img class="campus-photo" src="${campus.foto}" alt="${campus.nome}" loading="lazy" />`
    : '';

  btn.innerHTML = `
    <div class="campus-icon material-icons-round">location_on</div>
    ${foto}
    <strong>${campus.nome}</strong>
  `;

  return btn;
}

async function selecionarCampus(campus: Campus) {
  if (modoSelecaoCampus === 'permitidos' && !authService.canAccessCampus(state.session, campus.sigla)) {
    UIComponents.error('Seu usuário não possui acesso a este campus.');
    return;
  }

  if (state.campus?.id === campus.id) {
    showView('view-modulos');
    return;
  }
  
  state.campus = campus;

  const campusInfoHeader = byId('campus-info-header');
  
  if (campusInfoHeader) campusInfoHeader.classList.add('hidden');

  try {
    startGlobalLoading();
    const carregarDadosPromise = syncService.carregarLocais(campus.id);

    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Tempo de carregamento de dados esgotado.')), 10000)
    );

    const locais = await Promise.race([carregarDadosPromise, timeoutPromise]);

    state.locaisDb = locais as any[];
    console.log(`✅ ${state.locaisDb.length} locais carregados para ${campus.nome}`);

    byId('titulo-campus').textContent = campus.nome;
    byId('campus-nome-header').textContent = campus.nome;
    
    if (campusInfoHeader) {
      campusInfoHeader.classList.remove('hidden');
    }

    popularModulos();
    showView('view-modulos');
    history.pushState({ campus: campus.id }, '', `#${campus.id}`);

  } catch (error) {
    console.error('❌ Erro ao carregar campus:', error);
    UIComponents.error((error as Error).message || 'Erro ao carregar dados do campus.');
    voltarParaCampus();
    throw error;
  } finally {
    stopGlobalLoading();
  }
}

function voltarParaCampus() {
  abrirSelecaoCampus('todos');
}

function abrirSelecaoCampus(modo: ModoSelecaoCampus) {
  modoSelecaoCampus = modo;
  state.campus = null;
  state.modulo = null;

  byId('campus-info-header')?.classList.add('hidden');
  const switcher = byId('modulo-switcher');
  if (switcher) { switcher.classList.add('hidden'); switcher.innerHTML = ''; }

  popularCampus();
  showView('view-campus');
  history.pushState({ campus: null }, '', '#');
}

function trocarCampusPermitido() {
  abrirSelecaoCampus(state.session ? 'permitidos' : 'todos');
}

function voltarParaModulos() {
  if (!state.campus) {
    voltarParaCampus();
    return;
  }
  state.modulo = null;
  popularModulos();
  renderizarModuloSwitcher();
  showView('view-modulos');
  history.pushState({ campus: state.campus.id }, '', `#${state.campus.id}`);
}

function campusMaisProximo(latitude: number, longitude: number): Campus | null {
  let melhor: { campus: Campus; distancia: number } | null = null;
  for (const campus of listarCampus()) {
    const coord = CAMPUS_COORDS[campus.id];
    if (!coord) continue;
    const distancia = Math.hypot(latitude - coord.lat, longitude - coord.lon);
    if (!melhor || distancia < melhor.distancia) melhor = { campus, distancia };
  }
  return melhor?.campus || null;
}

async function selecionarCampusPorLocalizacao() {
  const hashAtual = window.location.hash.replace('#', '');
  if (!navigator.geolocation || hashAtual) return false;

  return await new Promise<boolean>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const campus = campusMaisProximo(position.coords.latitude, position.coords.longitude);
        if (!campus) {
          resolve(false);
          return;
        }
        selecionarCampus(campus).then(() => resolve(true)).catch(() => resolve(false));
      },
      () => resolve(false),
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 60 * 60 * 1000 }
    );
  });
}

// ========== MÓDULOS ==========
function popularModulos() {
  const grid = byId('modulos-grid');
  if (!grid) return;

  grid.innerHTML = '';

  const modulos = [
    { id: 'inventario', titulo: 'Inventário', desc: 'Leitura de bens', icon: 'inventory_2' },
    { id: 'verificacao', titulo: 'Verificação', desc: 'Análises do inventário', icon: 'checklist' },
    { id: 'movimentacao', titulo: 'Movimentação', desc: 'Informar origem e destino', icon: 'sync_alt' },
    { id: 'laudo', titulo: 'Solicitação de Laudo', desc: 'Abertura de pedidos', icon: 'description' },
    { id: 'base-dados', titulo: 'Base de dados', desc: 'Relatórios consolidados', icon: 'storage' },
    { id: 'locais', titulo: 'Locais', desc: 'Gerenciar sedes, blocos e salas', icon: 'location_city' },
    { id: 'usuarios', titulo: 'Usuários e Acessos', desc: 'Administração de perfis', icon: 'admin_panel_settings' }
  ].filter(modulo => authService.canAccessModulo(state.session, modulo.id, state.campus?.sigla));

  modulos.forEach(modulo => {
    const card = criarCard(
      modulo.titulo,
      modulo.desc,
      modulo.icon,
      () => selecionarModulo(modulo.id)
    );
    grid.appendChild(card);
  });
}

function criarCard(titulo: string, descricao: string, icon: string, onClick: () => void): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'card';
  btn.innerHTML = `
    <div class="card-icon"><span class="material-icons-round">${icon}</span></div>
    <div class="card-title"><strong>${titulo}</strong></div>
    <div class="card-desc">${descricao}</div>
  `;
  btn.addEventListener('click', onClick);
  return btn;
}

function selecionarModulo(moduloId: string) {
  if (!authService.canAccessModulo(state.session, moduloId, state.campus?.sigla)) {
    UIComponents.error('Seu perfil não possui acesso a este módulo.');
    return;
  }

  entrarModulo(moduloId);
}

function entrarModulo(moduloId: string) {
  state.modulo = moduloId;
  renderizarModuloSwitcher();
  
  const url = `#${state.campus?.id}/${moduloId}`;
  history.pushState({ campus: state.campus?.id, modulo: moduloId }, '', url);

  switch (moduloId) {
    case 'inventario':
      showView('view-localizacao');
      moduloInventario.inicializar();
      break;
    case 'verificacao':
      showView('view-verificacao');
      moduloVerificacao.inicializar();
      break;
    case 'movimentacao':
      showView('view-movimentacao');
      moduloMovimentacao.inicializar();
      break;
    case 'laudo':
      showView('view-laudo');
      moduloLaudo.inicializar();
      break;
    case 'base-dados':
      showView('view-base-dados');
      break;
    case 'locais':
      showView('view-locais');
      moduloLocais.inicializar();
      break;
    case 'usuarios':
      showView('view-usuarios');
      moduloUsuarios.inicializar();
      break;
  }
}

function renderizarModuloSwitcher() {
  const switcher = byId('modulo-switcher');
  if (!switcher || !state.campus) {
    switcher?.classList.add('hidden');
    return;
  }

  const modulos = [
    { id: 'inventario', titulo: 'Inventário' },
    { id: 'verificacao', titulo: 'Verificação' },
    { id: 'movimentacao', titulo: 'Movimentação' },
    { id: 'laudo', titulo: 'Laudo' },
    { id: 'base-dados', titulo: 'Base de dados' },
    { id: 'locais', titulo: 'Locais' },
    { id: 'usuarios', titulo: 'Usuários' },
  ].filter(modulo => authService.canAccessModulo(state.session, modulo.id, state.campus?.sigla));

  switcher.innerHTML = '';
  modulos.forEach(m => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `modulo-chip ${state.modulo === m.id ? 'active' : ''}`;
    btn.textContent = m.titulo;
    btn.addEventListener('click', () => selecionarModulo(m.id));
    switcher.appendChild(btn);
  });

  switcher.classList.remove('hidden');
}

// ========== HASH NAVIGATION ==========
async function processarHash() {
  const hash = window.location.hash.slice(1);
  if (!hash) {
    voltarParaCampus();
    return false;
  }

  const partes = hash
    .split('/')
    .filter(Boolean)
    .map(segment => {
      try {
        return decodeURIComponent(segment);
      } catch (error) {
        console.warn('Não foi possível decodificar segmento do hash:', segment, error);
        return segment;
      }
    });

  const campusId = partes[0];
  const moduloId = partes[1];
  const blocoId = partes[2];
  const andarId = partes[3];
  const salaId = partes[4];

  const campus = listarCampus().find(c => c.id === campusId);

  if (campus) {
    await selecionarCampus(campus);
    if (moduloId) {
      if (!authService.canAccessModulo(state.session, moduloId, state.campus?.sigla)) {
        UIComponents.error('Seu perfil não possui acesso a este módulo.');
        return true;
      }
      if (moduloId === 'inventario') {
        if (blocoId) state.bloco = blocoId;
        if (andarId) state.andar = andarId;
        if (salaId) state.sala = salaId;
      }

      entrarModulo(moduloId);

      if (moduloId === 'inventario' && blocoId && andarId && salaId) {
        moduloInventario.entrarInventarioLocal();
      }
    }
    return true;
  } else {
    voltarParaCampus();
    return false;
  }
}

function atualizarAuthUI() {
  const badge = byId('auth-badge');
  const btnLogin = byId('btn-login');
  const btnSenha = byId('btn-alterar-senha');
  const btnLogout = byId('btn-logout');

  if (state.session) {
    if (badge) {
      badge.textContent = `${labelPapel(state.session.papel)} · ${state.session.email}`;
      badge.classList.remove('hidden');
    }
    btnLogin?.classList.add('hidden');
    btnSenha?.classList.remove('hidden');
    btnLogout?.classList.remove('hidden');
  } else {
    badge?.classList.add('hidden');
    btnLogin?.classList.remove('hidden');
    btnSenha?.classList.add('hidden');
    btnLogout?.classList.add('hidden');
  }

  popularCampus();
  if (state.campus) popularModulos();
}

function abrirLogin(tab: 'login' | 'senha' = 'login') {
  const dialog = byId<HTMLDialogElement>('login-dialog');
  if (!dialog) return;
  trocarAuthTab(tab);
  dialog.showModal();
}

function trocarAuthTab(tab: 'login' | 'senha') {
  document.querySelectorAll<HTMLButtonElement>('[data-auth-tab]').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.authTab === tab);
  });
  byId('login-form')?.classList.toggle('hidden', tab !== 'login');
  byId('senha-form')?.classList.toggle('hidden', tab !== 'senha');
}

async function executarLogin(event: Event) {
  event.preventDefault();
  const status = byId('login-status');
  const email = byId<HTMLInputElement>('login-email')?.value.trim() || '';
  const password = byId<HTMLInputElement>('login-password')?.value || '';
  if (status) {
    status.classList.remove('hidden');
    status.textContent = 'Entrando...';
  }

  try {
    state.session = await authService.signIn(email, password);
    byId<HTMLDialogElement>('login-dialog')?.close();
    atualizarAuthUI();
    await direcionarAposLogin();
    UIComponents.success('Login realizado com sucesso!');
  } catch (error) {
    if (status) status.textContent = (error as Error).message;
  }
}

async function direcionarAposLogin() {
  const permitidos = campusPermitidosLogin();
  if (permitidos.length === 1) {
    modoSelecaoCampus = 'permitidos';
    await selecionarCampus(permitidos[0]);
    return;
  }

  if (permitidos.length > 1) {
    abrirSelecaoCampus('permitidos');
    return;
  }

  popularModulos();
}

async function alterarSenha(event: Event) {
  event.preventDefault();
  const status = byId('senha-status');
  const nova = byId<HTMLInputElement>('senha-nova')?.value || '';
  const confirma = byId<HTMLInputElement>('senha-confirma')?.value || '';
  if (status) {
    status.classList.remove('hidden');
    status.textContent = '';
  }
  if (nova !== confirma) {
    if (status) status.textContent = 'As senhas não coincidem.';
    return;
  }

  try {
    await authService.updatePassword(nova);
    if (status) status.textContent = 'Senha alterada com sucesso.';
  } catch (error) {
    if (status) status.textContent = (error as Error).message;
  }
}

async function executarLogout() {
  await authService.signOut();
  state.session = null;
  voltarParaCampus();
  atualizarAuthUI();
}

function configurarAuth() {
  byId('btn-login')?.addEventListener('click', () => abrirLogin('login'));
  byId('btn-alterar-senha')?.addEventListener('click', () => abrirLogin('senha'));
  byId('btn-logout')?.addEventListener('click', () => void executarLogout());
  byId('btn-fechar-login')?.addEventListener('click', () => byId<HTMLDialogElement>('login-dialog')?.close());
  byId('login-form')?.addEventListener('submit', executarLogin);
  byId('senha-form')?.addEventListener('submit', alterarSenha);
  document.querySelectorAll<HTMLButtonElement>('[data-auth-tab]').forEach(btn => {
    btn.addEventListener('click', () => trocarAuthTab(btn.dataset.authTab === 'senha' ? 'senha' : 'login'));
  });
}

function labelPapel(papel: string) {
  const labels: Record<string, string> = {
    admin: 'Admin',
    gestor: 'Gestor',
    inventariante: 'Inventariante',
  };
  return labels[papel] || papel;
}

// ========== SCANNER, SCROLL REVEAL, ETC... ==========
function configurarScanner() {
  byId('btn-ler-camera')?.addEventListener('click', () => {
    barcodeScanner.iniciar((codigo) => {
      byId<HTMLInputElement>('input-tombo').value = codigo;
      UIComponents.success(`Código detectado: ${codigo}`);
      setTimeout(() => moduloInventario.buscarBemPorTombo(), 500);
    }).catch(error => {
      console.error('Erro ao iniciar scanner:', error);
      UIComponents.error('Erro ao acessar câmera. Verifique as permissões.');
    });
  });
  byId('btn-fechar-scanner')?.addEventListener('click', () => barcodeScanner.parar());
}

function setupScrollReveal() {
  const items = document.querySelectorAll('.apresentacao-item');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) entry.target.classList.add('reveal');
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
  items.forEach(item => {
    item.classList.remove('reveal');
    observer.observe(item);
  });
}

// ========== INICIALIZAR QUANDO DOM PRONTO ==========
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Exportar para debugging no console
(window as any).__APP_STATE__ = state;
(window as any).__SYNC_SERVICE__ = syncService;
